import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import * as tracking from 'dibs-tracking';
import { graphql, createFragmentContainer } from 'react-relay/legacy';

import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import PinterestSVG from 'dibs-icons/exports/legacy/Pinterest';
import FacebookSVG from 'dibs-icons/exports/legacy/Facebook';
import EmailSVG from 'dibs-icons/exports/legacy/Email';
import iPhoneSVG from 'dibs-icons/exports/legacy/Iphone';

import { DESKTOP, MOBILE } from '../../../utils/deviceType';
import { isMobilePortrait } from '../../catalogHelpers';
import styles from '../../styles/catalogMenuBar.scss';
import {
    openSendToFriendModal,
    setErrorOccurred,
    toggleShareOptions,
} from '../../actionCreators/uiActionCreators';
import { getShareAction, getShareOptions } from '../../catalogShareHelpers';
import { EMAIL, FACEBOOK, PINTEREST, SMS } from '../../catalogConstants';

const shareOptionSVGs = {
    [EMAIL]: EmailSVG,
    [FACEBOOK]: FacebookSVG,
    [PINTEREST]: PinterestSVG,
    [SMS]: iPhoneSVG,
};

export class ShareOptionsPanelComponent extends Component {
    constructor(props) {
        super(props);

        this.state = {
            shareIconHovered: null,
        };

        this.setShareIconHovered = this.setShareIconHovered.bind(this);
        this.unsetShareIconHovered = this.unsetShareIconHovered.bind(this);
        this.onClickShare = this.onClickShare.bind(this);
        this.createShareOptionsIcons = this.createShareOptionsIcons.bind(this);
    }

    setShareIconHovered(shareIconHovered) {
        this.setState({ shareIconHovered });
    }

    unsetShareIconHovered() {
        this.setState({ shareIconHovered: null });
    }

    onClickShare() {
        const { shareIconHovered: shareMethod } = this.state;
        const { catalogPage, catalogShareImgURL } = this.props;
        const catalogTitle = catalogPage?.title;

        tracking.trackEvent({
            category: 'product catalog',
            action: `share via ${shareMethod} clicked`,
            label: catalogTitle || 'n/a',
        });

        if (shareMethod === EMAIL) {
            this.props.openSendToFriendModal();
            this.props.toggleShareOptions();
            return;
        }

        const shareResult = getShareAction(shareMethod)(
            getShareOptions({
                shareMethod,
                socialShareImgURL: catalogShareImgURL,
                title: catalogTitle,
            })
        );

        if (shareResult) {
            this.props.toggleShareOptions();
        } else {
            this.props.setErrorOccurred();
        }
    }

    createShareOptionsIcons() {
        const { deviceType } = this.props;
        const isMobile = deviceType === MOBILE;

        const shareOptions = [FACEBOOK, PINTEREST, EMAIL];

        // if Web Sharing API exists, add the SMS icon in the second place
        if (navigator && navigator.share) {
            shareOptions.splice(1, 0, SMS);
        }

        return shareOptions.map((shareOption, index) => {
            const SVG = shareOptionSVGs[shareOption];
            const iconWrapperStyle = isMobile
                ? styles.mobileWrapper
                : styles[`${shareOption.toLowerCase()}Wrapper`];
            const iconStyle = isMobile ? styles.mobileShareIcon : styles.shareIcon;
            return (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid, react/forbid-elements
                <a
                    href="#"
                    className={iconWrapperStyle}
                    data-tn={`share-option-icon-${shareOption}`}
                    key={index}
                    onMouseEnter={() => {
                        this.setShareIconHovered(shareOption);
                    }}
                    onMouseLeave={this.unsetShareIconHovered}
                    onClick={this.onClickShare}
                >
                    <SVG
                        className={classNames({
                            [iconStyle]: true,
                            [styles.iconHighlighted]:
                                deviceType === DESKTOP &&
                                this.state.shareIconHovered === shareOption,
                        })}
                    />
                </a>
            );
        });
    }

    render() {
        const { deviceType, screenOrientation, shareOptionsOpen } = this.props;
        const isPhonePortrait = isMobilePortrait(deviceType, screenOrientation);
        const isMobile = deviceType === MOBILE;

        const shareOptionsIcons = this.createShareOptionsIcons();

        if (isMobile) {
            return (
                <ModalContainer
                    isOpen={shareOptionsOpen}
                    onClose={this.props.toggleShareOptions}
                    modalSize={'fullscreen'}
                    dataTn={'shareOptionsModal'}
                >
                    <ModalCloseButton onClick={this.props.toggleShareOptions} />
                    <BarHeader title={'Share'} />
                    <div className={isPhonePortrait ? styles.phonePortrait : styles.phoneLandscape}>
                        {shareOptionsIcons}
                    </div>
                </ModalContainer>
            );
        }

        return (
            <div
                className={classNames({
                    [styles.menuBarShareOptionsPanel]: true,
                    shareOptionsPanel: true,
                })}
            >
                <div className={styles.shareVia}>Share via:</div>
                {shareOptionsIcons}
            </div>
        );
    }
}

ShareOptionsPanelComponent.propTypes = {
    // component props
    catalogPage: PropTypes.object,
    catalogShareImgURL: PropTypes.string,
    deviceType: PropTypes.string,
    // redux state
    screenOrientation: PropTypes.string,
    shareOptionsOpen: PropTypes.bool,
    // redux actions
    openSendToFriendModal: PropTypes.func,
    setErrorOccurred: PropTypes.func,
    toggleShareOptions: PropTypes.func,
};

const mapStateToProps = state => {
    return {
        screenOrientation: state.ui.screenOrientation,
        shareOptionsOpen: state.ui.shareOptionsOpen,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        openSendToFriendModal: () => dispatch(openSendToFriendModal()),
        setErrorOccurred: () => dispatch(setErrorOccurred()),
        toggleShareOptions: () => dispatch(toggleShareOptions()),
    };
};

export const ShareOptionsPanel = createFragmentContainer(
    connect(mapStateToProps, mapDispatchToProps)(ShareOptionsPanelComponent),
    {
        catalogPage: graphql`
            fragment ShareOptionsPanel_catalogPage on CatalogPage {
                title
            }
        `,
    }
);
